import { format } from 'date-fns';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ProductCreate } from '../../Inventory/Product';
import { EstimateAndQuoteCreateCustomerSelect } from '../EstimateAndQuote/EstimateAndQuoteCreateCustomerSelect';

import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import { CollapsibleBoxColumn } from '@components/CollapsibleBoxColumn';
import { FormSectionContainer } from '@components/Container';
import { CustomerDisplayCard } from '@components/CustomerDisplayCard';
import { ExpandingBoxColumn } from '@components/ExpandingBoxColumn';
import {
  FormikDateInput,
  FormikSelect,
  FormikTextArea,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { FormActionsBar } from '@components/FormActionsBar';
import { LineItemsTable } from '@components/LineItemsTable';
import { Modal } from '@components/Modal';
import { routes } from '@config/routes';
import { useFormSubmission } from '@hooks/useFormSubmission';
import { useLineItems } from '@hooks/useLineItems';
import { useNotification } from '@hooks/useNotification';
import { useSalesTaxEffectiveRates } from '@hooks/useSalesTaxEffectiveRates';
import { useTaxCalculation } from '@hooks/useTaxCalculation';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CustomerViewModel } from '@models/Customer';
import {
  recurringInvoiceDefaultValue,
  recurringInvoiceValidation,
  paymentDueDateSelectOptions,
  repeatTypeSelectOptions,
  weekdayScheduleSelectionOptions,
  monthdayScheduleSelectOptions,
  monthScheduleSelectOptions,
  recurrenceEndTypeSelectOptions,
  customIntervalTypeSelectOptions,
  timeZoneSelectOptions,
} from '@models/RecurringInvoice';
import {
  recurringInvoiceDetailDefaultValue,
  RecurringInvoiceDetailViewModel,
} from '@models/RecurringInvoiceDetail';
import {
  recurringInvoiceDetailSalesTaxDefaultValue,
  RecurringInvoiceDetailSalesTaxViewModel,
} from '@models/RecurringInvoiceDetailSalesTax';
import { ContactService } from '@services/Contact';
import { CustomerService } from '@services/Customer';
import { ProductService } from '@services/Product';
import { RecurringInvoiceService } from '@services/RecurringInvoice';

export const RecurringInvoiceCreate = () => {
  const { showInfo, showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation =
    location.state?.from || routes.RNC_RECURRING_INVOICES;

  const [customerContacts, setCustomerContacts] = useState<
    FormikSelectOption[]
  >([]);

  const [customerBillingAddresses, setCustomerBillingAddresses] = useState<
    FormikSelectOption[]
  >([]);
  const [customerShippingAddresses, setCustomerShippingAddresses] = useState<
    FormikSelectOption[]
  >([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerViewModel | null>(null);

  // Form submission hook
  const { setSubmitType, handlePostSubmitNavigation } = useFormSubmission({
    listRoute: routes.RNC_RECURRING_INVOICES,
    getEditRoute: (id) => `${routes.RNC_RECURRING_INVOICES}/${id}/edit`,
  });

  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  const {
    salesTaxes,
    salesTaxEffectiveRates,
    loadData: loadSalesTaxEffectiveRates,
  } = useSalesTaxEffectiveRates();

  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [products, setProducts] = useState<FormikSelectOption[]>([]);

  // Line items hook - manages recurring invoice details
  const {
    lineItems: recurringInvoiceDetails,
    setLineItems: setRecurringInvoiceDetails,
    addLineItem: handleAddRecurringInvoiceDetail,
    removeLineItem: handleRemoveRecurringInvoiceDetail,
    resetLineItem: handleResetRecurringInvoiceDetail,
    updateLineItemQuantity,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    getSalesTaxesBySalesTaxID,
  } = useLineItems<
    RecurringInvoiceDetailViewModel,
    RecurringInvoiceDetailSalesTaxViewModel
  >(
    {
      defaultLineItem: recurringInvoiceDetailDefaultValue,
      defaultSalesTax: recurringInvoiceDetailSalesTaxDefaultValue,
      salesTaxesKey: 'recurringInvoiceDetailSalesTaxes',
    },
    { onInfo: showInfo }
  );

  const [repeatType, setRepeatType] = useState<string | null>(null);
  const [customIntervalType, setCustomIntervalType] = useState<string | null>(
    null
  );
  const [recurrenceEndType, setRecurrenceEndType] = useState<string | null>(
    '1'
  );

  const [currentDate] = useState(() => {
    return format(new Date(), 'MM/dd/yyy');
  });

  // Tax calculation hook - manages tax exempt status
  const { setFormikPropsRef, getIsTaxExempt } = useTaxCalculation();

  const loadSelectedCustomer = (customerId: string, formikProps: any) => {
    if (customerId === '0') {
      setSelectedCustomer(null);
      formikProps.setFieldValue('contactID', 0);
      formikProps.setFieldValue('contactEmail', '');
      formikProps.setFieldValue('contactPhone', '');
      formikProps.setFieldValue('billingAddressID', 0);
      formikProps.setFieldValue('isSameShipping', false);
      formikProps.setFieldValue('shippingAddressID', 0);
      formikProps.setFieldValue('isTaxExempt', false);
      setCustomerContacts([{ value: '0', text: '' }]);
      setCustomerBillingAddresses([{ value: '0', text: '' }]);
      setCustomerShippingAddresses([{ value: '0', text: '' }]);

      // Recalculate taxes when no customer is selected (should apply taxes normally)
      setTimeout(() => {
        recalculateTaxAmountsForCustomer(null);
      }, 100);
    } else {
      CustomerService.getById(Number(customerId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          setSelectedCustomer(result.data);
          formikProps.setFieldValue('customerID', result.data.id);
          formikProps.setFieldValue(
            'contactID',
            result.data.primaryContact?.id || 0
          );
          formikProps.setFieldValue(
            'contactEmail',
            result.data.primaryContact?.email || ''
          );
          formikProps.setFieldValue(
            'contactPhone',
            result.data.primaryContact?.phone || ''
          );
          formikProps.setFieldValue(
            'billingAddressID',
            result.data.billingID || 0
          );
          formikProps.setFieldValue(
            'isSameShipping',
            result.data.isSameShipping
          );
          formikProps.setFieldValue(
            'shippingAddressID',
            result.data.shippingID || 0
          );
          formikProps.setFieldValue(
            'isTaxExempt',
            result.data.isTaxExempt || false
          );
          const successMessage = `Successfully fetched customer details`;
          console.log(successMessage);

          // Recalculate taxes based on new customer's tax exemption status
          setTimeout(() => {
            recalculateTaxAmountsForCustomer(result.data);
          }, 100);
        } else {
          showError(
            `Failed to get the customer details: ${result.errorMessages}`
          );
        }
      });

      CustomerService.getCustomerContactsSelectOptions(Number(customerId)).then(
        (result) => {
          if ('data' in result) {
            const customerContactsSelectOptions = [
              { value: '0', text: '' },
            ].concat(
              result.data.map((row) => ({
                value: row.value,
                text: row.text,
              }))
            );

            setCustomerContacts(customerContactsSelectOptions);
          } else {
            const errorMessage = `Failed to get customer contacts select options: ${result.errorMessages}`;
            console.error(errorMessage);
            showError(errorMessage);
          }
        }
      );

      CustomerService.getCustomerBillingAddressesSelectOptions(
        Number(customerId)
      ).then((result) => {
        if ('data' in result) {
          const customerBillingAddressesSelectOptions = [
            { value: '0', text: '' },
          ].concat(
            result.data.map((row) => ({
              value: row.value,
              text: row.text,
            }))
          );

          setCustomerBillingAddresses(customerBillingAddressesSelectOptions);
        } else {
          const errorMessage = `Failed to get customer billing addresses select options: ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });

      CustomerService.getCustomerShippingAddressesSelectOptions(
        Number(customerId)
      ).then((result) => {
        if ('data' in result) {
          const customerShippingAddressesSelectOptions = [
            { value: '0', text: '' },
          ].concat(
            result.data.map((row) => ({
              value: row.value,
              text: row.text,
            }))
          );

          setCustomerShippingAddresses(customerShippingAddressesSelectOptions);
        } else {
          const errorMessage = `Failed to get customer shipping addresses select options: ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadSelectedContact = (contactId: string, formikProps: any) => {
    if (contactId === '0') {
      formikProps.setFieldValue('contactEmail', '');
      formikProps.setFieldValue('contactPhone', '');
    } else {
      ContactService.getById(Number(contactId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          formikProps.setFieldValue('contactEmail', result.data.email || '');
          formikProps.setFieldValue('contactPhone', result.data.phone || '');
        } else {
          const errorMessage = `Failed to get contact details: ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadProducts = () => {
    ProductService.getProductSelectOptions().then((result) => {
      if ('data' in result) {
        const productsSelectOptions = [{ value: '0', text: '' }].concat(
          result.data.map((row) => ({
            value: row.value,
            text: row.text,
          }))
        );
        setProducts(productsSelectOptions);
      } else {
        const errorMessage = `Failed to load products select options: ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  // Recalculate tax amounts using the hook
  const recalculateTaxAmountsForCustomer = (
    customer?: CustomerViewModel | null,
    overrideTaxExempt?: boolean
  ) => {
    const customerToUse = customer !== undefined ? customer : selectedCustomer;
    const isTaxExempt =
      overrideTaxExempt !== undefined
        ? overrideTaxExempt
        : customerToUse?.isTaxExempt === true;
    recalculateTaxAmounts(isTaxExempt);
  };

  const loadSelectedProduct = (productId: string, index: number) => {
    const emptyRecurringInvoiceDetail: RecurringInvoiceDetailViewModel =
      recurringInvoiceDetailDefaultValue;
    if (productId === '0') {
      const updatedRecurringInvoiceDetails = [...recurringInvoiceDetails];
      updatedRecurringInvoiceDetails[index] = emptyRecurringInvoiceDetail;
      setRecurringInvoiceDetails(updatedRecurringInvoiceDetails);
    } else {
      ProductService.getById(Number(productId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          const recurringInvoiceDetailSalesTaxes = result.data.productSalesTaxes
            .map((o) => {
              const salesTaxEffectiveRate = salesTaxEffectiveRates.find(
                (i) => i.id == o.salesTaxId
              );
              return {
                salesTaxID: o.salesTaxId,
                salesTaxAbbreviation: o.salesTax.abbreviation,
                rate: salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0,
                salesTaxAmount: getIsTaxExempt()
                  ? 0
                  : (Number(
                      salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0
                    ) *
                      Number(result.data.price)) /
                    100,
              } as RecurringInvoiceDetailSalesTaxViewModel;
            })
            .concat([recurringInvoiceDetailSalesTaxDefaultValue]);

          const newRecurringInvoiceDetail: RecurringInvoiceDetailViewModel = {
            id: 0,
            recurringInvoiceID: 0,
            productID: result.data.id,
            product: result.data,
            description: result.data.description,
            quantity: 1,
            price: result.data.price,
            recurringInvoiceDetailSalesTaxes: recurringInvoiceDetailSalesTaxes,
            subTotal: result.data.price,
          };

          const updatedRecurringInvoiceDetails = [...recurringInvoiceDetails];
          updatedRecurringInvoiceDetails[index] = newRecurringInvoiceDetail;
          setRecurringInvoiceDetails(updatedRecurringInvoiceDetails);
        } else {
          const errorMessage = `Failed to get product details: ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  // Wrapper to adapt hook's updateLineItemQuantity to existing signature
  const handleUpdateRecurringInvoiceDetailQuantity = (
    index: number,
    quantity: number
  ) => {
    updateLineItemQuantity(index, quantity, getIsTaxExempt());
  };

  // Handle updating sales tax on a line item
  const handleUpdateRecurringInvoiceDetailSalesTax = (
    invoiceDetailIndex: number,
    invoiceDetailSalesTaxIndex: number,
    _invoiceDetailSalesTaxesLength: number,
    salesTaxID: number
  ) => {
    const salesTax = salesTaxEffectiveRates.find((o) => o.id == salesTaxID);
    updateLineItemSalesTax(
      invoiceDetailIndex,
      invoiceDetailSalesTaxIndex,
      salesTax,
      getIsTaxExempt()
    );
  };

  // Use hook's updateSalesTaxRates for updating tax rates
  const handleUpdateRecurringInvoiceDetailSalesTaxRates = () => {
    updateSalesTaxRates(salesTaxEffectiveRates, getIsTaxExempt());
  };

  useEffect(() => {
    handleUpdateRecurringInvoiceDetailSalesTaxRates();
  }, [salesTaxEffectiveRates]);

  useEffect(() => {
    loadProducts();
    loadSalesTaxEffectiveRates(new Date());
  }, []);

  return (
    <>
      <Box>
        <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
          <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
            Create Recurring Invoice
          </p>
        </div>
        <Formik
          initialValues={{
            ...recurringInvoiceDefaultValue,
            firstInvoiceDate: currentDate,
            recurringInvoiceNumber: 'Auto-generated',
            date: currentDate,
          }}
          validationSchema={recurringInvoiceValidation}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values, actions) => {
            const formattedValues = {
              ...values,
              date: format(new Date(values.date), 'yyyy-MM-dd'),
              firstInvoiceDate: format(
                new Date(values.firstInvoiceDate),
                'yyyy-MM-dd'
              ),
              paymentDueDate: Number(values.paymentDueDate),
              repeatType: Number(values.repeatType),
              recurrenceEndType: Number(values.recurrenceEndType),
            };

            if (formattedValues.repeatType === 1) {
              formattedValues.weekdaySchedule = undefined;
              formattedValues.monthdaySchedule = undefined;
              formattedValues.monthSchedule = undefined;
              formattedValues.customRepeatInterval = undefined;
              formattedValues.customIntervalType = undefined;
            } else if (formattedValues.repeatType === 2) {
              formattedValues.weekdaySchedule = Number(values.weekdaySchedule);
              formattedValues.monthdaySchedule = undefined;
              formattedValues.monthSchedule = undefined;
              formattedValues.customRepeatInterval = undefined;
              formattedValues.customIntervalType = undefined;
            } else if (formattedValues.repeatType === 3) {
              formattedValues.weekdaySchedule = undefined;
              formattedValues.monthdaySchedule = Number(
                values.monthdaySchedule
              );
              formattedValues.monthSchedule = undefined;
              formattedValues.customRepeatInterval = undefined;
              formattedValues.customIntervalType = undefined;
            } else if (formattedValues.repeatType === 4) {
              formattedValues.weekdaySchedule = undefined;
              formattedValues.monthdaySchedule = Number(
                values.monthdaySchedule
              );
              formattedValues.monthSchedule = Number(values.monthSchedule);
              formattedValues.customRepeatInterval = undefined;
              formattedValues.customIntervalType = undefined;
            } else if (formattedValues.repeatType === 5) {
              formattedValues.weekdaySchedule = undefined;
              formattedValues.monthdaySchedule = undefined;
              formattedValues.monthSchedule = undefined;
              formattedValues.customRepeatInterval = Number(
                values.customRepeatInterval
              );
              formattedValues.customIntervalType = Number(
                values.customIntervalType
              );
            }

            if (formattedValues.recurrenceEndType === 2) {
              formattedValues.recurrenceEndOn = format(
                new Date(values.recurrenceEndOn ?? ''),
                'yyyy-MM-dd'
              );
            } else {
              formattedValues.recurrenceEndOn = undefined;
            }

            formattedValues.recurringInvoiceDetails = recurringInvoiceDetails;

            try {
              RecurringInvoiceService.create(formattedValues)
                .then((result) => {
                  if (result.isSuccess) {
                    const successMessage = `Successfully created recurring invoice`;
                    showSuccess(successMessage);

                    if ('data' in result && result.data?.id) {
                      handlePostSubmitNavigation({
                        success: true,
                        id: result.data.id,
                      });
                    }
                  } else {
                    const errorMessage = `Failed to create recurring invoice: ${result.errorMessages}`;
                    console.error(errorMessage);
                    showError(errorMessage);
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            } catch (error) {
              console.error(error);
              actions.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => {
            // Store formik props in ref for access in helper functions
            setFormikPropsRef(formikProps);

            const handleRepeatTypeChange = (
              e: React.ChangeEvent<HTMLSelectElement>
            ) => {
              const selectedValue = e.target.value;
              formikProps.setFieldValue('repeatType', selectedValue);
              setRepeatType(selectedValue);
            };

            const handleCustomIntervalTypeChange = (
              e: React.ChangeEvent<HTMLSelectElement>
            ) => {
              const selectedValue = e.target.value;
              formikProps.setFieldValue('customIntervalType', selectedValue);
              setCustomIntervalType(selectedValue);
            };

            const handleRecurrenceEndTypeChange = (
              e: React.ChangeEvent<HTMLSelectElement>
            ) => {
              const selectedValue = e.target.value;
              formikProps.setFieldValue('recurrenceEndType', selectedValue);
              setRecurrenceEndType(selectedValue);
            };

            return (
              <>
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    {/* Calling Card - Only visible when left column is collapsed */}
                    {isLeftColumnCollapsed && selectedCustomer && (
                      <CustomerDisplayCard
                        selectedCustomer={selectedCustomer}
                        contactID={formikProps.values.contactID}
                        contactEmail={formikProps.values.contactEmail}
                        customerContacts={customerContacts}
                        onEditClick={() => setIsLeftColumnCollapsed(false)}
                      />
                    )}
                    <BoxRowColumn>
                      <CollapsibleBoxColumn
                        isCollapsed={isLeftColumnCollapsed}
                        onToggleCollapse={() =>
                          setIsLeftColumnCollapsed(!isLeftColumnCollapsed)
                        }
                        canCollapse={!!selectedCustomer}
                        collapseTitle="Collapse form"
                        disabledTitle="Please select a customer first"
                      >
                        <EstimateAndQuoteCreateCustomerSelect
                          preSelectedCustomerId={
                            selectedCustomer?.id?.toString() ||
                            formikProps.values.customerID?.toString()
                          }
                          onCustomerSelectChange={(customerId: string) => {
                            loadSelectedCustomer(customerId, formikProps);
                          }}
                        />

                        {selectedCustomer && (
                          <div className="mb-4">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  formikProps.values.isTaxExempt || false
                                }
                                onChange={(e) => {
                                  formikProps.setFieldValue(
                                    'isTaxExempt',
                                    e.target.checked
                                  );
                                  console.log(
                                    'Manual tax exemption toggle:',
                                    e.target.checked
                                  );
                                  // Recalculate tax amounts when tax exemption status changes
                                  setTimeout(() => {
                                    recalculateTaxAmountsForCustomer(
                                      selectedCustomer,
                                      e.target.checked
                                    );
                                  }, 0);
                                }}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Tax Exempt
                              </span>
                            </label>
                          </div>
                        )}

                        <FormikSelect
                          label="Contact Name"
                          name="contactID"
                          selection={customerContacts}
                          onChange={(
                            event: React.ChangeEvent<HTMLSelectElement>
                          ) => {
                            loadSelectedContact(
                              event.target.value,
                              formikProps
                            );
                          }}
                        />

                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikInput
                              label="Email"
                              name="contactEmail"
                              readonly={true}
                            />
                          </BoxColumn>

                          <BoxColumn>
                            <FormikInput
                              label="Phone"
                              name="contactPhone"
                              readonly={true}
                            />
                          </BoxColumn>
                        </BoxRowColumn>
                        <BoxColumn>
                          <FormikSelect
                            label="Billing Address"
                            name="billingAddressID"
                            selection={customerBillingAddresses}
                            onChange={(e: any) => {
                              formikProps.handleChange(e);
                              formikProps.setFieldValue('isSameShipping', true);
                            }}
                          />

                          {!formikProps.values.isSameShipping ? (
                            <FormikSelect
                              label="Shipping Address"
                              name="shippingAddressID"
                              selection={customerShippingAddresses}
                            />
                          ) : (
                            <>
                              <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                                Shipping Address
                              </label>
                              <i>Same as Billing Address</i>
                            </>
                          )}
                        </BoxColumn>

                        <BoxRowColumn>
                          <FormikInput
                            label="Invoice Number"
                            name="recurringInvoiceNumber"
                            readonly={true}
                          />

                          <FormikInput label="PO/SO Number" name="poNumber" />
                        </BoxRowColumn>

                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikDateInput
                              label="Invoice Date"
                              name="date"
                              type="date"
                              readonly={true}
                            />
                          </BoxColumn>

                          <BoxColumn>
                            <FormikSelect
                              label="Payment Due"
                              name="paymentDueDate"
                              selection={paymentDueDateSelectOptions}
                            />
                          </BoxColumn>
                        </BoxRowColumn>

                        <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                          Set Schedule
                        </label>
                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikSelect
                              label="Repeat this Invoice"
                              name="repeatType"
                              selection={repeatTypeSelectOptions}
                              onChange={handleRepeatTypeChange}
                              value={formikProps.values.repeatType}
                            />
                          </BoxColumn>

                          {repeatType === '5' && (
                            <BoxColumn>
                              <FormikInput
                                label="Every"
                                name="customRepeatInterval"
                              />
                            </BoxColumn>
                          )}

                          {repeatType === '2' && (
                            <BoxColumn>
                              <FormikSelect
                                label="Every"
                                name="weekdaySchedule"
                                selection={weekdayScheduleSelectionOptions}
                              />
                            </BoxColumn>
                          )}

                          {repeatType === '3' && (
                            <BoxColumn>
                              <FormikSelect
                                label="On the"
                                name="monthdaySchedule"
                                selection={monthdayScheduleSelectOptions}
                              />
                              of every month.
                            </BoxColumn>
                          )}
                        </BoxRowColumn>

                        {repeatType === '4' && (
                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikSelect
                                label="Every"
                                name="monthSchedule"
                                selection={monthScheduleSelectOptions}
                              />
                            </BoxColumn>
                            <BoxColumn>
                              <FormikSelect
                                label="On the"
                                name="monthdaySchedule"
                                selection={monthdayScheduleSelectOptions}
                              />
                              day of the month.
                            </BoxColumn>
                          </BoxRowColumn>
                        )}

                        {repeatType === '5' && (
                          <>
                            <BoxRowColumn>
                              <BoxColumn></BoxColumn>
                              <BoxColumn>
                                <FormikSelect
                                  label=""
                                  name="customIntervalType"
                                  selection={customIntervalTypeSelectOptions}
                                  onChange={handleCustomIntervalTypeChange}
                                />
                              </BoxColumn>
                            </BoxRowColumn>

                            {customIntervalType === '2' && ( //Week
                              <>
                                <BoxRowColumn>
                                  <BoxColumn></BoxColumn>
                                  <BoxColumn>
                                    <FormikSelect
                                      label="on"
                                      name="weekdaySchedule"
                                      selection={
                                        weekdayScheduleSelectionOptions
                                      }
                                    />
                                  </BoxColumn>
                                </BoxRowColumn>
                              </>
                            )}

                            {customIntervalType === '3' && ( //Month
                              <>
                                <BoxRowColumn>
                                  <BoxColumn></BoxColumn>
                                  <BoxColumn>
                                    <FormikSelect
                                      label="on the"
                                      name="monthdaySchedule"
                                      selection={monthdayScheduleSelectOptions}
                                    />
                                    day of the month
                                  </BoxColumn>
                                </BoxRowColumn>
                              </>
                            )}

                            {customIntervalType === '4' && ( //year
                              <>
                                <BoxRowColumn>
                                  <BoxColumn>
                                    <FormikSelect
                                      label="in"
                                      name="monthSchedule"
                                      selection={monthScheduleSelectOptions}
                                    />
                                  </BoxColumn>
                                  <BoxColumn>
                                    <FormikSelect
                                      label="on the"
                                      name="monthdaySchedule"
                                      selection={monthdayScheduleSelectOptions}
                                    />
                                    day of the month
                                  </BoxColumn>
                                </BoxRowColumn>
                              </>
                            )}
                          </>
                        )}

                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikDateInput
                              label="Create first invoice on"
                              name="firstInvoiceDate"
                            />
                          </BoxColumn>
                          <BoxColumn>
                            <FormikSelect
                              label="And end"
                              name="recurrenceEndType"
                              selection={recurrenceEndTypeSelectOptions}
                              onChange={handleRecurrenceEndTypeChange}
                            />
                          </BoxColumn>
                        </BoxRowColumn>
                        {recurrenceEndType === '1' && (
                          <>
                            <BoxRowColumn>
                              <BoxColumn></BoxColumn>
                              <BoxColumn>
                                <FormikInput
                                  label=""
                                  name="recurrenceEndAfter"
                                />
                                invoices
                              </BoxColumn>
                            </BoxRowColumn>
                          </>
                        )}
                        {recurrenceEndType === '2' && (
                          <>
                            <BoxRowColumn>
                              <BoxColumn></BoxColumn>
                              <BoxColumn>
                                <FormikDateInput
                                  label=""
                                  name="recurrenceEndOn"
                                />
                              </BoxColumn>
                            </BoxRowColumn>
                          </>
                        )}

                        <FormikSelect
                          label="Timezone"
                          name="timeZone"
                          selection={timeZoneSelectOptions}
                        />
                        <label className="block text-sm font-medium text-primary-700 py-0">
                          Set a time zone to ensure invoice delivery in the
                          morning based on the recipient{`'`}s time zone.
                        </label>

                        <FormikTextArea label="Notes" name="notes" />
                      </CollapsibleBoxColumn>

                      <ExpandingBoxColumn isExpanded={isLeftColumnCollapsed}>
                        <div className="py-4">
                          <LineItemsTable<
                            RecurringInvoiceDetailViewModel,
                            RecurringInvoiceDetailSalesTaxViewModel
                          >
                            lineItems={recurringInvoiceDetails}
                            products={products}
                            salesTaxes={salesTaxes || []}
                            salesTaxesKey="recurringInvoiceDetailSalesTaxes"
                            onProductSelect={(productId, index) =>
                              loadSelectedProduct(productId, index)
                            }
                            onQuantityChange={
                              handleUpdateRecurringInvoiceDetailQuantity
                            }
                            onSalesTaxChange={
                              handleUpdateRecurringInvoiceDetailSalesTax
                            }
                            onAddLineItem={handleAddRecurringInvoiceDetail}
                            onRemoveLineItem={
                              handleRemoveRecurringInvoiceDetail
                            }
                            onCreateProductClick={() =>
                              setShowCreateProduct(true)
                            }
                            getSalesTaxesBySalesTaxID={() =>
                              getSalesTaxesBySalesTaxID
                            }
                            formikValues={{
                              isTaxExempt: formikProps.values.isTaxExempt,
                            }}
                            showDiscount={false}
                          />
                        </div>

                        <div className="flex items-end justify-end h-full">
                          <FormActionsBar
                            onCancel={() => navigate(previousLocation)}
                            isSubmitting={formikProps.isSubmitting}
                            isValid={formikProps.isValid}
                            buttons={[
                              {
                                label: 'Save & Close',
                                submitType: 'saveAndClose',
                              },
                              { label: 'Save', submitType: 'save' },
                            ]}
                            onSubmitTypeChange={setSubmitType}
                          />
                        </div>
                      </ExpandingBoxColumn>
                    </BoxRowColumn>
                  </FormSectionContainer>
                </form>
              </>
            );
          }}
        </Formik>
      </Box>
      {showCreateProduct && (
        <>
          <Modal show={true}>
            <ProductCreate
              onClose={(productResultViewModel) => {
                setShowCreateProduct(false);

                if (productResultViewModel && productResultViewModel.data) {
                  const newProducts = [
                    ...products.slice(0, products.length - 1),
                    {
                      value: productResultViewModel.data.id.toString(),
                      text: productResultViewModel.data.name,
                    },
                  ];

                  setProducts(newProducts);

                  loadSelectedProduct(
                    productResultViewModel.data.id.toString(),
                    recurringInvoiceDetails.length - 1
                  );
                } else {
                  handleResetRecurringInvoiceDetail(
                    recurringInvoiceDetails.length - 1
                  );
                }
              }}
            />
          </Modal>
        </>
      )}
    </>
  );
};
